// Focus trap hook for modal/dialog components. Traps Tab/Shift+Tab within a container,
// handles Escape callback, locks body scroll, and restores focus on unmount.
// When `enabled=false`, the trap suspends entirely — no Esc handling, no
// Tab interception, no body scroll-lock, no focus transfer. Used when a
// layer-2 modal takes focus ownership above a still-mounted surface.
// When `lockScroll=false` (default true), the trap behaves identically
// EXCEPT it does not lock body scroll — used by the no-backdrop
// ActivityDrawer, where the main content stays visible AND scrollable
// because the drawer is a contextual panel, not a modal interruption.
//
// Stacked-modal safety (two traps mounted at once, e.g. a confirmation
// dialog over an open form, unmounting in ANY order):
// - The body scroll lock is a module-level REFCOUNT, not a per-trap
//   capture-and-restore: the first lock records the pre-lock overflow value
//   and sets 'hidden'; the style is restored only when the last locking trap
//   releases. A per-trap capture would let a lower modal restore '' under a
//   still-open dialog — or worse, capture 'hidden' from a sibling and wedge
//   the page unscrollable after a non-LIFO teardown.
// - Focus restore is ownership-gated: a trap only restores its previous
//   focus if it owns focus at cleanup (focus inside its container, or
//   already dropped to body because the container's DOM was removed). A
//   lower modal unmounting beneath an open aria-modal dialog must not yank
//   focus out to the background page. Residual limitation of the body arm:
//   an upper modal that swaps out its focused subtree in the same commit as
//   a lower modal's unmount can still trigger a transient restore; modals
//   showing terminal states should autoFocus a control so they hold focus
//   through the swap.

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Shared body scroll-lock refcount across every mounted trap (all Modal
// instances render in the same document, so the lock is inherently global
// state — the counter just makes that explicit and teardown-order-proof).
let scrollLockCount = 0;
let preLockOverflow = '';

function acquireScrollLock(): void {
  if (scrollLockCount === 0) {
    preLockOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount++;
}

function releaseScrollLock(): void {
  // Defensive floor at zero: an unbalanced release must never go negative
  // and make a later acquire skip locking.
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = preLockOverflow;
  }
}

interface UseFocusTrapOptions {
  onEscape: () => void;
  /** Default true. Set to false to suspend the trap without unmounting. */
  enabled?: boolean;
  /** Default true (locks body scroll on mount, restores on unmount).
   *  Set to false for a contextual panel that must leave the page
   *  scrollable (the no-backdrop ActivityDrawer). */
  lockScroll?: boolean;
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  options: UseFocusTrapOptions
) {
  const { onEscape, enabled = true, lockScroll = true } = options;
  const previousFocusRef = useRef<Element | null>(null);
  // Store onEscape in a ref so callback identity changes don't re-trigger the effect
  const onEscapeRef = useRef(onEscape);

  // Keep onEscapeRef current after each render so the keydown handler always
  // calls the latest callback without needing to re-attach the listener.
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    previousFocusRef.current = document.activeElement;

    // Focus first focusable element, or container itself
    const focusableElements = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      container.tabIndex = -1;
      container.focus();
    }

    // Lock body scroll (unless the caller opts out — a no-backdrop
    // contextual panel must leave the page scrollable). Refcounted so
    // stacked modals can unmount in any order without wedging or
    // prematurely releasing the lock.
    if (lockScroll) {
      acquireScrollLock();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onEscapeRef.current();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = container!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      if (lockScroll) {
        releaseScrollLock();
      }

      // Restore focus, but only if this trap still OWNS it: focus is inside
      // this trap's container, or has already dropped to body/nowhere
      // (a real unmount removes the container's DOM before this cleanup
      // runs, which blurs the focused element). If another still-open trap
      // owns focus — a dialog stacked above this one — leave it alone.
      const active = document.activeElement;
      const ownsFocus =
        active === null || active === document.body || container.contains(active);
      const prev = previousFocusRef.current;
      if (ownsFocus && prev && prev instanceof HTMLElement && prev.isConnected) {
        prev.focus();
      }
    };
  }, [containerRef, enabled, lockScroll]);
}
